if (!customElements.get('steps-component')) {
  class StepsComponent extends HTMLElement {
    constructor() {
      super();

      this.header = document.querySelector(
        '#shopify-section-header header, .shopify-section-group-header-group header',
      );
      this.wrapper = this.querySelector('[data-steps-wrapper]');
      this.content = this.querySelector('[data-steps-content]');
      this.bgLayers = Array.from(
        this.querySelectorAll('[data-steps-bg-layer]'),
      );
      this.cardImgs = Array.from(
        this.querySelectorAll('[data-steps-card-img]'),
      );
      this.items = Array.from(this.querySelectorAll('[data-steps-item]'));
      this.progressBar = this.querySelector('[data-steps-progress-bar]');
      this.dots = this.progressBar
        ? Array.from(this.progressBar.querySelectorAll('.circle-dot'))
        : [];
      this.badgeCount = this.querySelector('[data-steps-badge-count]');
      this.count = this.items.length;
      this.sectionHeight = null;
      this.lastActiveIndex = -1;
      this.mobileProgress = 0;
      this._snapTween = null;
      this.scrollTrigger = null;
      this._touchHandlers = null;
      this.isMobileMode = null;
      this.lastWidth = null;
      this.lastHeight = null;
      this._onScroll = null;
      this._headerRaf = null;
    }

    connectedCallback() {
      window.addEventListener('load', () => {
        gsap.registerPlugin(ScrollTrigger);
        this.init();
        window.addEventListener(
          'resize',
          debounce(() => {
            this.update();
          }, 200),
        );

        // Single source of truth for header transparency: the section's real
        // position vs the header band. Runs in both modes, rAF-throttled.
        this._onScroll = () => {
          if (this._headerRaf) return;
          this._headerRaf = requestAnimationFrame(() => {
            this._headerRaf = null;
            this.updateHeaderState();
          });
        };
        window.addEventListener('scroll', this._onScroll, { passive: true });
        this.updateHeaderState();
      });
    }

    get isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    init() {
      this.sortByIndex(this.bgLayers);
      this.sortByIndex(this.cardImgs);

      // z-indexes: step 0 on top for all modes
      this.bgLayers.forEach(
        (layer, i) => (layer.style.zIndex = this.count - i),
      );
      this.cardImgs.forEach((img, i) => (img.style.zIndex = this.count - i));
      this.items.forEach((item) => (item.style.opacity = '1'));

      this.isMobileMode = this.isMobile;
      this.lastWidth = window.innerWidth;
      this.lastHeight = window.innerHeight;
      this.initMode();
    }

    initMode() {
      if (this.isMobileMode) {
        this.initMobile();
      } else {
        this.setSectionHeight();
        this.updateProgress(0);
        this.createAnimations();
      }
    }

    update() {
      const mobile = this.isMobile;

      // Crossed the mobile/desktop breakpoint: rebuild the active mode
      if (mobile !== this.isMobileMode) {
        this.teardown();
        this.isMobileMode = mobile;
        this.lastWidth = window.innerWidth;
        this.lastHeight = window.innerHeight;
        this.initMode();
        this.updateHeaderState();
        return;
      }

      if (mobile) {
        // Recompute on width OR height change (height feeds the 100vh min).
        // Bail only when nothing changed (avoids no-op reflow).
        if (
          window.innerWidth === this.lastWidth &&
          window.innerHeight === this.lastHeight
        ) {
          return;
        }
        this.lastWidth = window.innerWidth;
        this.lastHeight = window.innerHeight;
        const progress = this.mobileProgress;
        this.setMobileLayout();
        this.mobileProgress = progress;
        this.updateProgress(progress);
      } else {
        this.lastWidth = window.innerWidth;
        this.lastHeight = window.innerHeight;
        this.setSectionHeight();
        ScrollTrigger.refresh();
      }
    }

    // Undo everything mode-specific so the other mode can start clean
    teardown() {
      if (this._snapTween) this._snapTween.kill();

      if (this.scrollTrigger) {
        this.scrollTrigger.kill(true);
        this.scrollTrigger = null;
      }

      if (this._touchHandlers) {
        const { onStart, onMove, onEnd } = this._touchHandlers;
        this.wrapper.removeEventListener('touchstart', onStart);
        this.wrapper.removeEventListener('touchmove', onMove);
        this.wrapper.removeEventListener('touchend', onEnd);
        this._touchHandlers = null;
      }

      this.setHeaderImmersive(false);

      // Clear inline layout styles set by the previous mode
      this.style.height = '';
      this.wrapper.style.height = '';
      this.content.style.top = '';
      this.content.style.height = '';
      this.items.forEach((item) => (item.style.translate = ''));
      this.mobileProgress = 0;
      this.lastActiveIndex = -1;
    }

    sortByIndex(arr) {
      arr.sort((a, b) => +a.dataset.index - +b.dataset.index);
    }

    setSectionHeight() {
      // Desktop heights are CSS-driven (--section-height / --steps-count);
      // this number only feeds the translate and ScrollTrigger end math
      this.sectionHeight = window.innerHeight;
    }

    // ── MOBILE: manual touch → horizontal translation ─────────────

    setMobileLayout() {
      const GAP = 16; // 1.6rem below the image, fixed regardless of viewport
      const DOTS_SPACE = 80; // fixed reserve below content for the dots row

      // Position content strip immediately below the image wrapper
      const imageWrapper = this.querySelector('[data-steps-image-wrapper]');
      if (imageWrapper && this.content) {
        const wrapperRect = imageWrapper.getBoundingClientRect();
        const sectionRect = this.getBoundingClientRect();
        const imageBottom = wrapperRect.bottom - sectionRect.top;
        this.content.style.top = `${imageBottom + GAP}px`;
        this.content.style.height = 'auto';

        // Temporarily give items position:relative so the container can measure them
        this.items.forEach((item) => (item.style.position = 'relative'));
        const maxItemHeight = Math.max(
          ...this.items.map((item) => item.offsetHeight),
        );
        this.items.forEach((item) => (item.style.position = ''));

        // Set content height explicitly (overflow:hidden collapses it otherwise)
        this.content.style.height = `${maxItemHeight}px`;

        // Content-driven height, but never shorter than the viewport (100vh)
        const totalHeight = imageBottom + GAP + maxItemHeight + DOTS_SPACE;
        this.sectionHeight = Math.max(totalHeight, window.innerHeight);
      } else {
        this.sectionHeight = window.innerHeight;
      }

      this.style.height = `${this.sectionHeight}px`;
      this.wrapper.style.height = `${this.sectionHeight}px`;
    }

    initMobile() {
      this.setMobileLayout();

      this.mobileProgress = 0;

      let touchStartX = 0;
      let touchStartY = 0;
      let progressAtStart = 0;
      let isHorizontal = null;

      const onStart = (e) => {
        if (this._snapTween) this._snapTween.kill();
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        progressAtStart = this.mobileProgress;
        isHorizontal = null;
      };

      const onMove = (e) => {
        const dx = e.touches[0].clientX - touchStartX;
        const dy = e.touches[0].clientY - touchStartY;

        // Determine gesture direction on first non-trivial movement
        if (isHorizontal === null) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          isHorizontal = Math.abs(dx) > Math.abs(dy);
        }

        if (!isHorizontal) return; // let the browser handle vertical scroll

        // Block page scroll for horizontal swipes
        e.preventDefault();

        const progress = progressAtStart - dx / (window.innerWidth * 0.4);
        this.mobileProgress = this.clamp(progress, 0, this.count - 1);
        this.updateProgress(this.mobileProgress);
      };

      const onEnd = () => {
        if (!isHorizontal) return;
        const target = Math.round(this.mobileProgress);
        const obj = { p: this.mobileProgress };
        this._snapTween = gsap.to(obj, {
          p: target,
          duration: 0.5,
          ease: 'power2.out',
          onUpdate: () => {
            this.mobileProgress = obj.p;
            this.updateProgress(obj.p);
          },
        });
      };

      // passive: false on touchmove so we can call preventDefault
      this.wrapper.addEventListener('touchstart', onStart, { passive: true });
      this.wrapper.addEventListener('touchmove', onMove, { passive: false });
      this.wrapper.addEventListener('touchend', onEnd, { passive: true });
      this._touchHandlers = { onStart, onMove, onEnd };

      this.updateProgress(0);
    }

    // ── SHARED PROGRESS UPDATE ───────────────────────────────────

    clamp(v, lo, hi) {
      return Math.max(lo, Math.min(hi, v));
    }

    updateProgress(progress) {
      const FADE = 10;
      // Mobile: wipe reveals from the right as you swipe left
      const dir = this.isMobile ? 'to left' : 'to top';

      // 1. Background mask wipe
      this.bgLayers.forEach((bg, i) => {
        const clip = this.clamp((progress - i) * 100, 0, 100);
        // Only apply the soft fade once a transition is actually in progress
        const fade = clip > 0 ? FADE : 0;
        const mask = `linear-gradient(${dir}, transparent 0%, transparent ${clip}%, black ${
          clip + fade
        }%, black 100%)`;
        bg.style.maskImage = mask;
        bg.style.webkitMaskImage = mask;
      });

      // 2. Card image crossfade — snappy projector cut:
      // Image holds until SWITCH_AT fraction into the scroll to the next step
      // (text box is nearly in place by then), then fades quickly over SNAP.
      const SWITCH_AT = 0.75;
      const SNAP = 0.18;
      this.cardImgs.forEach((img, i) => {
        const past = progress - i - SWITCH_AT;
        const opacity = past <= 0 ? 1 : this.clamp(1 - past / SNAP, 0, 1);
        img.style.opacity = opacity.toFixed(3);
      });

      // 3. Text box travel: vertical on desktop, horizontal on mobile
      if (this.isMobile) {
        this.items.forEach((item, i) => {
          const offset = (i - progress) * window.innerWidth;
          item.style.translate = `${offset}px 0`;
        });
      } else {
        this.items.forEach((item, i) => {
          const offset = (i - progress) * this.sectionHeight;
          item.style.translate = `0 ${offset}px`;
        });
      }

      // 4. Active step
      const activeIndex = this.clamp(Math.round(progress), 0, this.count - 1);
      if (activeIndex !== this.lastActiveIndex) {
        this.updateActiveStep(activeIndex);
      }
    }

    updateActiveStep(activeIndex) {
      this.items.forEach((item, i) => {
        item.classList.toggle('is-active', i === activeIndex);
      });

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i <= activeIndex);
        dot.classList.toggle('current', i === activeIndex);
      });

      const color = getComputedStyle(this.items[activeIndex])
        .getPropertyValue('--color-background')
        .trim();
      this.style.setProperty('--current-color', color);

      if (this.badgeCount) {
        this.badgeCount.textContent = `Step ${activeIndex + 1}`;
      }

      this.lastActiveIndex = activeIndex;
    }

    setHeaderImmersive(enter) {
      if (!this.header) return;
      this.header.classList.toggle('is-immersive', enter);
    }

    // Immersive when the section covers the header band [0, headerHeight].
    // Uses the section's live rect so it stays correct on every scroll frame,
    // in both desktop (pinned) and mobile modes, regardless of step direction.
    updateHeaderState() {
      if (!this.header) return;
      const rect = this.wrapper.getBoundingClientRect();
      const headerH = this.header.getBoundingClientRect().height;
      const overlap = rect.top <= headerH && rect.bottom > headerH;
      this.setHeaderImmersive(overlap);
    }

    // ── DESKTOP: vertical GSAP pin + snap ────────────────────────

    createAnimations() {
      const updateFromTrigger = (self) => {
        this.updateProgress(self.progress * (this.count - 1));
      };

      this.scrollTrigger = ScrollTrigger.create({
        trigger: this.wrapper,
        start: 'top top',
        end: () => `+=${this.sectionHeight * (this.count - 1)}px`,
        pin: true,
        pinSpacing: false,
        snap: {
          snapTo: 1 / (this.count - 1),
          duration: { min: 0.5, max: 0.8 },
          ease: 'power2.inOut',
          delay: 0,
        },
        onUpdate: updateFromTrigger,
        onEnter: updateFromTrigger,
        onEnterBack: updateFromTrigger,
      });
    }
  }

  customElements.define('steps-component', StepsComponent);
}
